import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback } from "react";
import { IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import { useMutationRequestWorkLogEditing } from "../../../../query/workLog/mutations/useMutationRequestWorkLogEditing";
import { PublicCommentField } from "../../components/PublicCommentField";

type FormData = {
  comment: string;
};

type Props = {
  workLog: IWorkLog;
  clientName?: string;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalRequestEditing: React.FC<Props> = ({
  workLog,
  clientName = "the client",
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [requestEditing, requestEditingMeta] =
    useMutationRequestWorkLogEditing();

  const handleRequestEditing = useCallback(
    async (formData: FormData) => {
      if (!formData.comment.trim()) {
        openSnackbar({
          type: "warning",
          message: "Please provide details about your editing request"
        });
        return;
      }

      requestEditing(
        {
          workLogId: workLog.workLogId,
          comment: formData.comment
        },
        {
          onSuccess: () => {
            modalProps.onClose({ success: true });
            openSnackbar({
              icon: { name: "check" },
              message: "Editing request sent to client"
            });
          },
          onError: () => {
            openSnackbar({
              type: "warning",
              message: "Failed to request editing. Please try again."
            });
          }
        }
      );
    },
    [openSnackbar, modalProps, requestEditing, workLog.workLogId]
  );

  return (
    <WSModal title="Request editing" {...modalProps}>
      <WSForm<FormData>
        onSubmit={handleRequestEditing}
        defaultValues={{ comment: "" }}
      >
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            Are you sure you want to request editing?
          </WSText.ParagraphSm>

          <WSAlert
            theme="info"
            title="About work log editing"
            icon="info-circle"
          >
            Once a work log is submitted, it cannot be edited while in review.
            This asks your client to send the work log back to you for
            corrections. Once they do, you can make changes and resubmit for
            approval.
            {"\n\n"}
            Note: late submissions can delay approval and payment.
          </WSAlert>

          <PublicCommentField
            label="Comment *"
            description={`Please provide specific details that will inform ${clientName} about the request`}
            placeholder="Provide specific details about the decision"
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "No, go back",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                label: "Yes, request editing",
                loading: requestEditingMeta.isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalRequestEditing = () =>
  useWSModalCustom(ModalRequestEditing);
